import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc
} from "firebase/firestore";
import { db } from "./firebase";
import { useEffect, useState } from "react";
import { useSession } from "./context/SessionContext";
import { useNavigate } from "react-router-dom";
import SettingsModal from "./components/SettingsModal";
import { CLUB_ID, THEME_COLOR } from "./config/club";
import Swal from "sweetalert2";
import SessionSummaryModal from "./components/SessionSummaryModal";

export default function SelectSession() {
  const [sessions, setSessions] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const { setSessionId, setSession } = useSession();
  const [selectedSession, setSelectedSession] = useState(null);
  const [openMonths, setOpenMonths] = useState({});
  const toggleMonth = (month) => {
    setOpenMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };
  const navigate = useNavigate();
  const clubId = CLUB_ID;
  const Toast = Swal.mixin({
    toast: true,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
  const ToastShow = (type, message) => {
    Toast.fire({
      icon: type,
      title: message,
    });
  };
  // Helper to create transparent versions of your theme color
  const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;

  const getDuprId = async (userId) => {
    if (!userId) return "";

    const userSnap = await getDoc(doc(db, "users", userId));

    if (!userSnap.exists()) return "";

    return (userSnap.data()?.dupr || "").toUpperCase();
  };

  const loadSessions = async () => {
    const q = query(
      collection(db, "clubs", clubId, "sessions"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    loadSessions();
  }, [clubId]);

  const activeSessions = sessions.filter(s => s.status === "active");
  const endedSessions = sessions.filter(s => s.status === "ended");
  const groupSessionsByMonth = (sessions) => {
    const groups = {};

    sessions.forEach((s) => {
      if (!s.createdAt?.seconds) return;

      const date = new Date(s.createdAt.seconds * 1000);
      const monthKey = date.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });

      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(s);
    });

    return groups;
  };

  const groupedEndedSessions = groupSessionsByMonth(endedSessions);
  return (
    <div className="container py-5" style={{ maxWidth: "900px" }}>
      <div className="mb-4">
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="btn d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill border-0 shadow-sm transition-all"
          style={{ backgroundColor: `${THEME_COLOR}25`, color: THEME_COLOR }}
        >
          <i className="bi bi-arrow-left-short fs-4"></i>
          <span className="fw-bold small">Back to Dashboard</span>
        </button>
      </div>
      {/* HEADER SECTION */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-5 gap-3">
        <div>
          <h1 className="fw-bold mb-1" style={{ color: THEME_COLOR, letterSpacing: '-1.5px', fontSize: '2.5rem' }}>
            Sessions
          </h1>
          <p className="text-muted m-0 opacity-75">Manage and monitor your club activities</p>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          {/* Navigation Actions */}
          {/* {[
    { label: "Players", path: "/admin/players", icon: "bi-people-fill" },
    { label: "Jersey Orders", path: "/admin/jersey", icon: "bi-tag-fill" },
    { label: "Tournament", path: "/admin/registrations", icon: "bi-trophy-fill" },
  ].map((btn) => (
    <button
      key={btn.label}
      className="btn px-4 py-2 rounded-pill fw-bold border-0 transition-all"
      style={{
        backgroundColor: `${THEME_COLOR}15`,
        color: THEME_COLOR,
        fontSize: '0.9rem'
      }}
      onClick={() => navigate(btn.path)}
    >
      <i className={`${btn.icon} me-2`}></i>
      {btn.label}
    </button>
  ))} */}

          {/* Primary Action - Pushed to the end on larger screens */}
          <button
            className="btn shadow-sm px-4 py-2 rounded-pill fw-bold border-0 text-white ms-md-auto"
            style={{ backgroundColor: THEME_COLOR }}
            onClick={() => setShowSettings(true)}
          >
            <i className="bi bi-plus-lg me-1"></i> New Session
          </button>
        </div>
      </div>

      <div className="row g-4">
        {/* 🟢 ACTIVE SESSIONS */}
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981', marginRight: 10 }}></div>
            <h6 className="text-uppercase small fw-bold m-0 text-muted tracking-wider">Live Now</h6>
          </div>

          {activeSessions.length === 0 && (
            <div className="rounded-4 p-5 text-center border-0" style={{ backgroundColor: themeAlpha('05'), border: `1px dashed ${themeAlpha('20')}` }}>
              <p className="text-muted m-0">No sessions are currently active.</p>
            </div>
          )}

          <div className="row g-3">
            {activeSessions.map(s => (
              <div key={s.id} className="col-md-6 col-lg-4">
                <div
                  className="card h-100 border-0 shadow-sm p-4 hover-lift"
                  style={{
                    borderRadius: '24px',
                    background: `linear-gradient(135deg, var(--card-bg) 0%, ${themeAlpha('08')} 100%)`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${themeAlpha('10')}`
                  }}
                  onClick={() => {
                    setSessionId(s.id);
                    setSession(s);
                    navigate("/admin");
                  }}
                >
                  <div className="mb-4">
                    <span className="badge rounded-pill mb-2" style={{ backgroundColor: themeAlpha('15'), color: THEME_COLOR }}>
                      Active
                    </span>
                    <h4 className="fw-bold d-block" style={{ color: 'var(--text-color)' }}>{s.name}</h4>
                  </div>

                  <div className="mt-auto d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {new Date(s.createdAt?.seconds * 1000).toLocaleString("en-US", { dateStyle: "medium", })}
                    </small>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 35, height: 35, backgroundColor: THEME_COLOR, color: 'white' }}
                    >
                      →
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 🔴 ENDED SESSIONS (Minimalist Laptop List) */}
        {/* 🔴 SESSION HISTORY */}
        {/* 🔴 SESSION HISTORY */}
        {endedSessions.length > 0 && (
          <div className="col-12 mt-5">

            <h6 className="text-uppercase small fw-bold text-muted mb-3">
              Session History
            </h6>

            {Object.entries(groupedEndedSessions).map(([month, monthSessions]) => {

              const isOpen = openMonths[month] ?? true;

              return (
                <div key={month} className="mb-3">

                  {/* Month Header */}
                  <div
                    onClick={() => toggleMonth(month)}
                    className="d-flex justify-content-between align-items-center px-4 py-3 rounded-4 month-header"
                    style={{
                      cursor: "pointer",
                      background: "var(--card-bg)",
                      border: "1px solid var(--border-color)",
                      transition: "all 0.2s ease"
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">

                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 28,
                          height: 28,
                          background: `${THEME_COLOR}`,
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 700
                        }}
                      >
                        {isOpen ? "−" : "+"}
                      </div>

                      <div>
                        <span className="fw-bold" style={{ color: "var(--text-color)" }}>
                          {month}
                        </span>

                        <div className="small text-muted">
                          {monthSessions.length} session{monthSessions.length > 1 && "s"}
                        </div>
                      </div>

                    </div>

                    <i
                      className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
                      style={{ color: "var(--text-muted)" }}
                    />
                  </div>

                  {/* Session List */}
                  {isOpen && (
                    <div
                      className="mt-2 rounded-4 overflow-hidden"
                      style={{
                        background: "var(--card-bg)",
                        border: "1px solid var(--border-color)"
                      }}
                    >

                      {monthSessions.map((s, index) => (
                        <div
                          key={s.id}
                          className={`d-flex justify-content-between align-items-center py-3 px-4 ${index !== monthSessions.length - 1 ? "border-bottom" : ""
                            }`}
                          style={{ transition: "background 0.2s" }}
                        >

                          <div>
                            <span
                              className="fw-bold d-block"
                              style={{ color: "var(--text-color)" }}
                            >
                              {s.name}
                            </span>

                            <small className="text-muted">
                              {new Date(
                                s.createdAt?.seconds * 1000
                              ).toLocaleDateString("en-US", {
                                day: "numeric",
                                month: "short",
                                year: "numeric"
                              })}
                            </small>
                          </div>

                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm px-3 rounded-pill fw-bold border"
                              onClick={() => setSelectedSession(s)}
                              style={{
                                color: "var(--text-muted)",
                                borderColor: "var(--border-color)"
                              }}
                            >
                              Summary
                            </button>

                            <button
                              className="btn btn-sm px-3 rounded-pill fw-bold border"
                              style={{
                                color: THEME_COLOR,
                                borderColor: `${THEME_COLOR}55`,
                                background: `${THEME_COLOR}10`
                              }}
                              onClick={async () => {
                                try {
                                  const matchesRef = collection(
                                    db,
                                    "clubs",
                                    clubId,
                                    "sessions",
                                    s.id,
                                    "matches"
                                  );

                                  const q = query(matchesRef, orderBy("createdAt", "asc"));
                                  const snap = await getDocs(q);

                                  if (snap.empty) {
                                    ToastShow("info", "No match history found.");
                                    return;
                                  }

                                  const matches = await Promise.all(
                                    snap.docs.map(async (matchDoc) => {
                                      const data = matchDoc.data();
                                      const [teamAGame1 = "", teamBGame1 = ""] = (data.score || "").split("-");
                                      const players = data.players || [];

                                      const a1 = players[0] || {};
                                      const a2 = players[1] || {};
                                      const b1 = players[2] || {};
                                      const b2 = players[3] || {};

                                      const [
                                        a1Dupr,
                                        a2Dupr,
                                        b1Dupr,
                                        b2Dupr
                                      ] = await Promise.all([
                                        getDuprId(a1.id),
                                        getDuprId(a2.id),
                                        getDuprId(b1.id),
                                        getDuprId(b2.id)
                                      ]);

                                      return {
                                        matchType: data.matchType || "D",
                                        event: s.name,
                                        date: data.startedAt?.toDate?.().toISOString().split("T")[0] || "",

                                        playerA1: a1.name || "",
                                        playerA1DuprId: a1Dupr,
                                        playerA1ExternalId: "",

                                        playerA2: a2.name || "",
                                        playerA2DuprId: a2Dupr,
                                        playerA2ExternalId: "",

                                        playerB1: b1.name || "",
                                        playerB1DuprId: b1Dupr,
                                        playerB1ExternalId: "",

                                        playerB2: b2.name || "",
                                        playerB2DuprId: b2Dupr,
                                        playerB2ExternalId: "",

                                        teamAGame1: data.score?.split("-")?.[0] || "",
                                        teamBGame1: data.score?.split("-")?.[1] || "",

                                        teamAGame2: "",
                                        teamBGame2: "",

                                        teamAGame3: "",
                                        teamBGame3: "",

                                        teamAGame4: "",
                                        teamBGame4: "",

                                        teamAGame5: "",
                                        teamBGame5: "",

                                        location: "",
                                        scoreType: "SIDEOUT"
                                      };
                                    })
                                  );

                                  const headers = [
                                    "matchType",
                                    "event",
                                    "date",
                                    "playerA1",
                                    "playerA1DuprId",
                                    "playerA1ExternalId",
                                    "playerA2",
                                    "playerA2DuprId",
                                    "playerA2ExternalId",
                                    "playerB1",
                                    "playerB1DuprId",
                                    "playerB1ExternalId",
                                    "playerB2",
                                    "playerB2DuprId",
                                    "playerB2ExternalId",
                                    "teamAGame1",
                                    "teamBGame1",
                                    "teamAGame2",
                                    "teamBGame2",
                                    "teamAGame3",
                                    "teamBGame3",
                                    "teamAGame4",
                                    "teamBGame4",
                                    "teamAGame5",
                                    "teamBGame5",
                                    "location",
                                    "scoreType"
                                  ];

                                  // const rows = matches.map((row) =>
                                  //   headers
                                  //     .map(key => `"${String(row[key] ?? "").replace(/"/g, '""')}"`)
                                  //     .join(",")
                                  // );

                                  const rows = matches.map((row) =>
                                    headers
                                      .map(key => String(row[key] ?? ""))
                                      .join(",")
                                  );

                                  const csvContent = [headers, ...rows].join("\n");

                                  const blob = new Blob([csvContent], {
                                    type: "text/csv;charset=utf-8;"
                                  });

                                  const url = URL.createObjectURL(blob);

                                  const link = document.createElement("a");

                                  link.href = url;

                                  link.setAttribute(
                                    "download",
                                    `${s.name.replace(/\s+/g, "_")}_match_history.csv`
                                  );

                                  document.body.appendChild(link);

                                  link.click();

                                  document.body.removeChild(link);

                                  URL.revokeObjectURL(url);

                                  ToastShow("success", "Match history downloaded.");
                                } catch (err) {
                                  console.error(err);
                                  ToastShow("error", "Failed to download match history.");
                                }
                              }}
                            >
                              <i className="bi bi-download me-1"></i>
                              Match History
                            </button>
                          </div>

                        </div>
                      ))}

                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}
      </div>

      {showSettings && (
        <SettingsModal
          show
          ToastShow={ToastShow}
          clubId={clubId}
          defaultTab="sessions"
          onClose={() => {
            setShowSettings(false);
            loadSessions();
          }}
        />
      )}
      {selectedSession && (
        <SessionSummaryModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}
      {/* Adding a small global style for the hover effect */}
      <style>{`
        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px ${themeAlpha('15')} !important;
          border-color: ${themeAlpha('30')} !important;
        }
      `}</style>
    </div>
  );
}